// Plan → Grid: the visual assignment board, ported from the legacy artifact's
// Guides view (guide rows × date columns, availability-tinted cells, sticky
// name rail) and upgraded from a read-mostly matrix to a direct assignment
// surface: EVERY cell is clickable. Clicking a guide-day opens that day's
// slots with one-tap Stage/Unstage; clicking an open slot in the top rail
// opens its candidate list (✨ preferred first, then no-response, ❌ shown but
// never assignable — eligibility.ts is the shared source of truth).
//
// Staging writes the same `staged` draft map as the List view, so drafts,
// auto-save, and the Notion → Peek publish pipeline apply unchanged.

import { memo, useMemo, useState } from "react";
import { AlertTriangle, Sparkles, X } from "lucide-react";
import type { GuideRef, PlanSlot } from "@/lib/load";
import { candidatesFor, type AvailTier, type SlotCandidates } from "@/lib/eligibility";
import type { SuggestionMap } from "@/lib/suggest";
import { GUIDES, guideByPeekId, lookupGuideByAnyName } from "@/config/guides";
import { productIdForTour } from "@/lib/eligibility";
import { guideRefById } from "@/lib/load";
import { slotStartMin } from "@/lib/dates";
import type { Guide, Region } from "@/types";

const REGION_ORDER: Record<Region, number> = { Flagstaff: 0, Prescott: 1, Tucson: 2 };

const MARKET_CELL: Record<string, string> = {
  FLG: "bg-emerald-100 text-emerald-800",
  PSCT: "bg-amber-100 text-amber-800",
  TUS: "bg-sky-100 text-sky-800",
};
const REGION_ACCENT: Record<Region, string> = {
  Flagstaff: "border-l-emerald-500",
  Prescott: "border-l-amber-500",
  Tucson: "border-l-sky-500",
};

/** Compact tour glyph for a 40px cell (legacy tourCode). */
const TOUR_CODES: Record<string, string> = {
  "80a1896b-62c1-4d84-a6f4-c8ddf65ff074": "Mu",
  "0ced34cb-0eb9-4f59-bc48-f5cb72b72187": "18+",
  "b419e3fd-85f0-416d-9018-6a6f5d0426fd": "66",
  "1898909d-ba15-436a-8c4f-452945d70f9f": "HH",
  "8d66ea83-c5af-42ea-90ec-9ba78f05d7f2": "HH",
  "0bc9822a-4109-461c-89bf-41ac494c61f7": "Hist",
  "2dae269d-e8a0-4afb-a926-0bae94468d37": "Mu",
  "7c6e9626-35fd-4e72-887a-f8fb29a04cb8": "HH",
};

function tourCode(tour: string): string {
  const pid = productIdForTour(tour);
  if (pid && TOUR_CODES[pid]) return TOUR_CODES[pid];
  return (tour.split(" ").pop() || "?").slice(0, 4);
}

/** Compact start time for a 40px cell: "7p", "7:30p" (legacy style). Uses
 * slotStartMin so date-only Notion rows fall back to the START TIME label. */
function compactTime(slot: PlanSlot): string {
  const min = slotStartMin(slot);
  if (min == null) return slot.startLabel || slot.hhmm || "?";
  const h = Math.floor(min / 60);
  const m = min % 60;
  const ap = h >= 12 ? "p" : "a";
  const h12 = h % 12 || 12;
  return m ? `${h12}:${String(m).padStart(2, "0")}${ap}` : `${h12}${ap}`;
}

/** Who effectively holds a slot: draft first, then Notion's assignment. */
function effectiveGuideId(slot: PlanSlot, staged: Record<string, GuideRef>): string | null {
  const st = staged[slot.pageId];
  if (st) return st.id;
  if (slot.assignedGuidePeekId) return slot.assignedGuidePeekId;
  for (const n of slot.assignedGuideNames) {
    const g = lookupGuideByAnyName(n);
    if (g) return g.id;
  }
  return null;
}

function effectiveGuideName(slot: PlanSlot, staged: Record<string, GuideRef>): string | null {
  const id = effectiveGuideId(slot, staged);
  if (!id) return null;
  return guideRefById(id)?.peekName ?? slot.assignedGuideNames[0] ?? null;
}

type ModalState =
  | { type: "guideDay"; guideId: string; date: string }
  | { type: "slot"; pageId: string }
  | null;

function PlanBoard({
  slots,
  staged,
  suggestions,
  onStage,
}: {
  slots: PlanSlot[];
  staged: Record<string, GuideRef>;
  suggestions: SuggestionMap;
  onStage: (slot: PlanSlot, guide: GuideRef | null) => void;
}) {
  const [modal, setModal] = useState<ModalState>(null);

  const dates = useMemo(
    () => [...new Set(slots.map((s) => s.date))].sort(),
    [slots],
  );

  const byDay = useMemo(() => {
    const m = new Map<string, PlanSlot[]>();
    for (const s of slots) {
      if (!m.has(s.date)) m.set(s.date, []);
      m.get(s.date)!.push(s);
    }
    for (const list of m.values()) list.sort((a, b) => (slotStartMin(a) ?? 9999) - (slotStartMin(b) ?? 9999) || a.hhmm.localeCompare(b.hhmm));
    return m;
  }, [slots]);

  const candBySlot = useMemo(() => {
    const m = new Map<string, SlotCandidates>();
    for (const s of slots) m.set(s.pageId, candidatesFor(s));
    return m;
  }, [slots]);

  const tierFor = (slot: PlanSlot, guideId: string): AvailTier | null => {
    const c = candBySlot.get(slot.pageId);
    if (!c) return null;
    if (c.unavailable.some((g) => g.id === guideId)) return "unavailable";
    if (c.preferred.some((g) => g.id === guideId)) return "preferred";
    if (c.open.some((g) => g.id === guideId)) return "open";
    return null;
  };

  // Active roster, region-grouped — plus benched guides who still hold shifts
  // in the window (hiding them would hide real assignments).
  const rows = useMemo(() => {
    const holders = new Set(slots.map((s) => effectiveGuideId(s, staged)).filter(Boolean));
    return GUIDES.filter((g) => !g.inactive || holders.has(g.id)).sort(
      (a, b) =>
        REGION_ORDER[a.region] - REGION_ORDER[b.region] ||
        a.peekName.localeCompare(b.peekName),
    );
  }, [slots, staged]);

  const countBy = useMemo(() => {
    const m: Record<string, number> = {};
    for (const s of slots) {
      const id = effectiveGuideId(s, staged);
      if (id) m[id] = (m[id] || 0) + 1;
    }
    return m;
  }, [slots, staged]);

  const openByDay = useMemo(() => {
    const m = new Map<string, PlanSlot[]>();
    for (const s of slots) {
      // Assigned names that don't resolve to a roster id (ex-guides, admins)
      // still mean the slot is HELD — never offer it in the open rail.
      if (s.excluded || effectiveGuideId(s, staged) || s.assignedGuideNames.length > 0) continue;
      if (!m.has(s.date)) m.set(s.date, []);
      m.get(s.date)!.push(s);
    }
    for (const list of m.values()) list.sort((a, b) => (slotStartMin(a) ?? 9999) - (slotStartMin(b) ?? 9999) || a.hhmm.localeCompare(b.hhmm));
    return m;
  }, [slots, staged]);

  const openCount = [...openByDay.values()].reduce((n, l) => n + l.length, 0);

  if (slots.length === 0) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-slate-100 bg-slate-50 px-4 py-2 text-[11px] text-slate-500">
        <span className="text-sm font-semibold text-slate-700">Assignment grid</span>
        <span>click any cell to assign</span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm border border-amber-300 bg-amber-100" /> ✨ preferred
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm border border-slate-200 bg-white" /> no response (assignable)
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="board-hatch inline-block h-3 w-3 rounded-sm border border-red-200" /> marked ❌
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded-sm border border-emerald-300 bg-emerald-100" /> assigned (colored by market)
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="border-separate border-spacing-0 text-[10px]">
          <thead>
            <tr>
              <th className="sticky left-0 z-20 border-b border-r border-slate-200 bg-white px-3 py-1 text-left text-[11px] font-semibold text-slate-500">
                Guide
              </th>
              {dates.map((d) => (
                <th key={d} className="min-w-[44px] border-b border-slate-200 bg-white px-1 py-1 text-center font-medium text-slate-500">
                  <div className="text-[9px] uppercase">{dow(d)}</div>
                  <div className="text-[11px] font-bold text-slate-700">{Number(d.slice(8, 10))}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Open-slots rail: everything still needing a guide, tap to fill. */}
            <tr>
              <th className="sticky left-0 z-10 border-b border-r border-slate-200 bg-red-50 px-3 py-1.5 text-left align-top">
                <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-semibold text-red-700">
                  <AlertTriangle size={11} /> Open ({openCount})
                </span>
              </th>
              {dates.map((d) => (
                <td key={d} className="border-b border-slate-200 bg-red-50/40 p-0.5 align-top">
                  <div className="flex flex-col gap-0.5">
                    {(openByDay.get(d) ?? []).map((s) => (
                      <button
                        key={s.pageId}
                        onClick={() => setModal({ type: "slot", pageId: s.pageId })}
                        title={`${s.startLabel} ${s.tour} — open, click to assign`}
                        className="rounded border border-dashed border-red-400 bg-white px-0.5 py-0.5 leading-tight text-red-700 hover:bg-red-100"
                      >
                        <div className="font-bold">{tourCode(s.tour)}</div>
                        <div>{compactTime(s)}</div>
                      </button>
                    ))}
                  </div>
                </td>
              ))}
            </tr>

            {rows.map((g) => (
              <tr key={g.id} className="group">
                <th
                  className={
                    "sticky left-0 z-10 whitespace-nowrap border-b border-r border-slate-200 border-l-[3px] bg-white px-3 py-1.5 text-left group-hover:bg-slate-50 " +
                    REGION_ACCENT[g.region]
                  }
                >
                  <span className="text-[11px] font-semibold text-slate-800">{g.peekName}</span>
                  <span className="ml-1.5 text-[9px] font-medium text-slate-400">
                    {g.region.slice(0, 4).toUpperCase()} · {countBy[g.id] || 0}
                  </span>
                  {g.inactive && <span className="ml-1 text-[9px] text-red-500">benched</span>}
                </th>
                {dates.map((d) => (
                  <GuideDayCell
                    key={d}
                    guide={g}
                    date={d}
                    daySlots={byDay.get(d) ?? []}
                    staged={staged}
                    tierFor={tierFor}
                    onOpen={() => setModal({ type: "guideDay", guideId: g.id, date: d })}
                  />
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal?.type === "guideDay" && (
        <GuideDayModal
          guide={guideByPeekId[modal.guideId] ?? null}
          date={modal.date}
          daySlots={byDay.get(modal.date) ?? []}
          staged={staged}
          tierFor={tierFor}
          onStage={onStage}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "slot" && (
        <SlotModal
          slot={slots.find((s) => s.pageId === modal.pageId) ?? null}
          staged={staged}
          candidates={candBySlot.get(modal.pageId) ?? null}
          suggestion={suggestions[modal.pageId] ?? null}
          onStage={onStage}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}

function GuideDayCell({
  guide,
  date,
  daySlots,
  staged,
  tierFor,
  onOpen,
}: {
  guide: Guide;
  date: string;
  daySlots: PlanSlot[];
  staged: Record<string, GuideRef>;
  tierFor: (slot: PlanSlot, guideId: string) => AvailTier | null;
  onOpen: () => void;
}) {
  const mine = daySlots.filter((s) => effectiveGuideId(s, staged) === guide.id);

  if (mine.length > 0) {
    return (
      <td className="border-b border-slate-100 p-0.5 align-top">
        <div className="flex flex-col gap-0.5">
          {mine.map((s) => {
            const tier = tierFor(s, guide.id);
            const ring =
              tier === "preferred"
                ? "ring-2 ring-inset ring-amber-400"
                : tier === "unavailable"
                  ? "ring-2 ring-inset ring-red-500"
                  : "";
            const isDraft = !!staged[s.pageId];
            return (
              <button
                key={s.pageId}
                onClick={onOpen}
                title={`${s.startLabel} ${s.tour}${isDraft ? " (draft)" : ""}${tier === "unavailable" ? " — CONFLICT: marked ❌" : tier === "preferred" ? " — honors ✨" : ""}`}
                className={
                  "rounded px-0.5 py-0.5 leading-tight " +
                  (s.market ? MARKET_CELL[s.market] : "bg-slate-100 text-slate-600") +
                  " " +
                  ring +
                  (isDraft ? " border border-dashed border-slate-500" : "")
                }
              >
                <div className="font-bold">{tourCode(s.tour)}</div>
                <div>{compactTime(s)}</div>
              </button>
            );
          })}
        </div>
      </td>
    );
  }

  // No assignment this day: availability tint aggregated across the day's slots.
  let hasAny = false;
  let hasPref = false;
  let hasOpen = false;
  for (const s of daySlots) {
    const t = tierFor(s, guide.id);
    if (t == null) continue;
    hasAny = true;
    if (t === "preferred") hasPref = true;
    if (t === "open") hasOpen = true;
  }
  if (!hasAny) {
    return <td className="border-b border-slate-100 bg-slate-50/60" />;
  }
  const cls = hasPref
    ? "cursor-pointer bg-amber-100 text-amber-700 hover:bg-amber-200"
    : hasOpen
      ? "cursor-pointer bg-white text-slate-300 hover:bg-slate-100"
      : "board-hatch cursor-pointer text-red-600";
  return (
    <td
      onClick={onOpen}
      title={
        hasPref
          ? `${guide.peekName} marked ✨ on ${date} — click to assign`
          : hasOpen
            ? `${guide.peekName} hasn't responded for ${date} — still assignable`
            : `${guide.peekName} marked ❌ on ${date}`
      }
      className={"border-b border-slate-100 text-center align-middle " + cls}
    >
      {hasPref ? "✨" : hasOpen ? "" : "✗"}
    </td>
  );
}

function tierBadge(tier: AvailTier | null) {
  if (tier === "preferred")
    return <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">✨ preferred</span>;
  if (tier === "open")
    return <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-500">no response</span>;
  if (tier === "unavailable")
    return <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">❌ unavailable</span>;
  return <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-400">not qualified</span>;
}

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-4 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="text-sm font-semibold text-slate-800">{title}</div>
          <button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Guide-day modal: every slot that day, one-tap Stage/Unstage for this guide. */
function GuideDayModal({
  guide,
  date,
  daySlots,
  staged,
  tierFor,
  onStage,
  onClose,
}: {
  guide: Guide | null;
  date: string;
  daySlots: PlanSlot[];
  staged: Record<string, GuideRef>;
  tierFor: (slot: PlanSlot, guideId: string) => AvailTier | null;
  onStage: (slot: PlanSlot, guide: GuideRef | null) => void;
  onClose: () => void;
}) {
  if (!guide) return null;
  const ref = guideRefById(guide.id);
  return (
    <ModalShell title={<>{guide.peekName} — {longDay(date)}</>} onClose={onClose}>
      {daySlots.length === 0 && <div className="text-sm text-slate-500">No tours this day.</div>}
      <div className="space-y-2">
        {daySlots.map((s) => {
          const tier = tierFor(s, guide.id);
          const holderId = effectiveGuideId(s, staged);
          const holderName = effectiveGuideName(s, staged);
          const isMine = holderId === guide.id;
          const isDraft = !!staged[s.pageId];
          const assignable = tier === "preferred" || tier === "open";
          return (
            <div key={s.pageId} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 p-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">
                  {s.startLabel || s.hhmm}
                  <span className={"ml-2 rounded px-1 py-0.5 text-[10px] font-semibold " + (s.market ? MARKET_CELL[s.market] : "bg-slate-100 text-slate-500")}>
                    {s.market ?? "?"}
                  </span>
                  <span className="ml-1 text-xs text-slate-500">{s.tour}</span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {tierBadge(tier)}
                  {holderName && !isMine && (
                    <span className="text-[10px] text-slate-500">
                      currently: {holderName}
                      {isDraft ? " (draft)" : ""}
                    </span>
                  )}
                  {isMine && (
                    <span className="text-[10px] font-medium text-emerald-600">
                      {isDraft ? "staged (draft)" : "assigned in Notion"}
                    </span>
                  )}
                </div>
              </div>
              {isMine && isDraft ? (
                <button
                  onClick={() => onStage(s, null)}
                  className="shrink-0 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                >
                  Unstage
                </button>
              ) : (
                <button
                  onClick={() => ref && onStage(s, ref)}
                  disabled={!assignable || !ref || (isMine && !isDraft)}
                  title={
                    tier === "unavailable"
                      ? `${guide.peekName} marked ❌ — not assignable`
                      : isMine && !isDraft
                        ? "Already assigned in Notion"
                        : holderName && !isMine
                          ? `Replaces ${holderName} in the draft`
                          : undefined
                  }
                  className="shrink-0 rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  {holderName && !isMine ? "Replace" : "Stage"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </ModalShell>
  );
}

/** Open-slot modal: the slot's candidates by tier, one-tap Stage. */
function SlotModal({
  slot,
  staged,
  candidates,
  suggestion,
  onStage,
  onClose,
}: {
  slot: PlanSlot | null;
  staged: Record<string, GuideRef>;
  candidates: SlotCandidates | null;
  suggestion: { guide: GuideRef; tier: "preferred" | "open" } | null;
  onStage: (slot: PlanSlot, guide: GuideRef | null) => void;
  onClose: () => void;
}) {
  if (!slot || !candidates) return null;
  const holderName = effectiveGuideName(slot, staged);
  const stagedRef = staged[slot.pageId] ?? null;

  const row = (g: GuideRef, tier: AvailTier) => {
    const isStaged = stagedRef?.id === g.id;
    return (
      <div key={g.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 p-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-sm font-medium text-slate-800">{g.peekName}</span>
          {guideByPeekId[g.id]?.inactive && (
            <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-500">benched</span>
          )}
          {tierBadge(tier)}
          {suggestion?.guide.id === g.id && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-700">
              <Sparkles size={10} /> suggested
            </span>
          )}
          {isStaged && <span className="text-[10px] font-medium text-emerald-600">staged</span>}
        </div>
        {isStaged ? (
          <button
            onClick={() => onStage(slot, null)}
            className="shrink-0 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
          >
            Unstage
          </button>
        ) : (
          <button
            onClick={() => onStage(slot, g)}
            disabled={tier === "unavailable"}
            className="shrink-0 rounded-md bg-slate-900 px-2 py-1 text-xs font-medium text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Stage
          </button>
        )}
      </div>
    );
  };

  return (
    <ModalShell
      title={
        <>
          {longDay(slot.date)} · {slot.startLabel || slot.hhmm} — {slot.tour}
          {holderName && <span className="ml-2 text-xs font-normal text-slate-500">currently: {holderName}</span>}
        </>
      }
      onClose={onClose}
    >
      <div className="space-y-2">
        {candidates.preferred.map((g) => row(g, "preferred"))}
        {candidates.open.map((g) => row(g, "open"))}
        {candidates.unavailable.map((g) => row(g, "unavailable"))}
        {candidates.preferred.length + candidates.open.length + candidates.unavailable.length === 0 && (
          <div className="text-sm text-slate-500">No eligible guides for this tour.</div>
        )}
      </div>
    </ModalShell>
  );
}

function dow(ymd: string): string {
  return new Date(`${ymd}T12:00:00`).toLocaleDateString("en-US", { weekday: "narrow" });
}

function longDay(ymd: string): string {
  return new Date(`${ymd}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

// Memoized: PlanList re-renders on every draft-name/notes keystroke; the
// ~800-cell grid only needs to re-render when slots/staged/suggestions change
// (onStage is a stable useCallback in PlanList).
export default memo(PlanBoard);
